import React, { useState, useEffect } from "react";
import {
  X,
  Users,
  Filter,
  Settings,
  Play,
  AlertCircle,
  CheckCircle,
  Info,
} from "lucide-react";
import FilterPresetSelector from "./FilterPresetSelector";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const AutoAssignLeadsModal = ({ isOpen, onClose, onSuccess, teamId }) => {
  const { isDarkMode } = useTheme();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Step 1: Filter Selection
  const [filterPresets, setFilterPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  const [advancedFilters, setAdvancedFilters] = useState(null);

  // Step 2: Assignment Settings
  const [leadsPerUser, setLeadsPerUser] = useState(10);
  const [minCycleCount, setMinCycleCount] = useState(4);
  const [assignmentStrategy, setAssignmentStrategy] = useState("round_robin");

  // Step 3: User Pool Selection
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");

  // Preview
  const [previewData, setPreviewData] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen, teamId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError("");

      const [presetsRes, userRes] = await Promise.all([
        apiService.getFilterPresets("lead"),
        apiService.getMe(),
      ]);

      setFilterPresets(presetsRes.data || []);
      setCurrentUser(userRes.user);

      if (teamId) {
        const membersRes = await apiService.getTeamMembers(teamId);
        const members = membersRes.members || membersRes.data || [];

        const teamUsers = members
          .filter((member) => member.status === "active" && member.user)
          .map((member) => ({
            id: member.user.id,
            name: member.user.name,
            email: member.user.email,
            profileImageUrl: member.user.profileImageUrl,
            role: member.role,
          }));

        setUsers(teamUsers);

        if (teamUsers.length === 0) {
          setError(
            "No active team members found. Please add members to this team first.",
          );
        }
      } else {
        const usersRes = await apiService.getCompanyUsers();
        const companyUsers = usersRes.data || usersRes.users || [];
        setUsers(companyUsers);

        if (companyUsers.length === 0) {
          setError("No users found in your company.");
        }
      }
    } catch (err) {
      console.error("Error loading data:", err);
      const errorMessage =
        err.response?.data?.message || err.message || "Unknown error";
      setError(`Failed to load data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPreset = async (presetId) => {
    if (!presetId) {
      setSelectedPresetId(null);
      setAdvancedFilters(null);
      return;
    }

    try {
      const response = await apiService.getFilterPreset(presetId);
      setSelectedPresetId(presetId);
      setAdvancedFilters(response.data.filterConfig);
    } catch (err) {
      setError("Failed to load filter preset");
    }
  };

  const handleDeletePreset = async (presetId) => {
    try {
      await apiService.deleteFilterPreset(presetId);
      setFilterPresets(filterPresets.filter((p) => p.id !== presetId));
      if (selectedPresetId === presetId) {
        setSelectedPresetId(null);
        setAdvancedFilters(null);
      }
    } catch (err) {
      setError("Failed to delete preset");
    }
  };

  const handleSetDefaultPreset = async (presetId) => {
    try {
      await apiService.updateFilterPreset(presetId, { isDefault: true });
      await loadInitialData();
    } catch (err) {
      setError("Failed to set default preset");
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const toggleAllUsers = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map((u) => u.id));
    }
  };

  const getPreviewData = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await apiService.previewAutoAssignment({
        filterPresetId: selectedPresetId,
        advancedFilters: advancedFilters,
        leadsPerUser: parseInt(leadsPerUser),
        userIds: selectedUsers,
        minCycleCount: parseInt(minCycleCount),
        assignmentStrategy,
        teamId,
      });

      setPreviewData(response.data);
      setStep(4);
    } catch (err) {
      setError(err.message || "Failed to generate preview");
    } finally {
      setLoading(false);
    }
  };

  const executeAssignment = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const response = await apiService.executeAutoAssignment({
        filterPresetId: selectedPresetId,
        advancedFilters: advancedFilters,
        leadsPerUser: parseInt(leadsPerUser),
        userIds: selectedUsers,
        minCycleCount: parseInt(minCycleCount),
        assignmentStrategy,
        teamId,
      });

      setSuccess(
        `Successfully assigned ${response.data.totalAssigned} leads to ${response.data.usersAssigned} users`,
      );

      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.message || "Failed to execute assignment");
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    if (!userSearchQuery) return true;
    const query = userSearchQuery.toLowerCase();
    return (
      user.name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query)
    );
  });

  const canProceed = () => {
    switch (step) {
      case 1:
        return advancedFilters !== null;
      case 2:
        return leadsPerUser > 0 && minCycleCount >= 1;
      case 3:
        return selectedUsers.length > 0;
      default:
        return true;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div
        className={`w-full max-w-4xl rounded-xl shadow-2xl flex flex-col border ${
          isDarkMode
            ? "bg-[#0C0E12] border-gray-700"
            : "bg-white border-gray-200"
        }`}
        style={{ height: "calc(100vh - 4rem)", maxHeight: "900px" }}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b flex-shrink-0 ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <Users size={24} className="text-blue-400" />
            <div>
              <h2
                className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Auto-Assign Leads
              </h2>
              <p
                className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Step {step} of 4:{" "}
                {step === 1
                  ? "Select Filter"
                  : step === 2
                    ? "Assignment Settings"
                    : step === 3
                      ? "Select Users"
                      : "Review & Execute"}
              </p>
              {teamId && (
                <p className="text-xs text-blue-400 mt-1">
                  Assigning to team members only
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white hover:bg-gray-800"
                : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            }`}
          >
            <X size={24} />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4 pb-4 flex-shrink-0">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex-1">
                <div
                  className={`h-2 rounded-full ${
                    s <= step
                      ? "bg-blue-600"
                      : isDarkMode
                        ? "bg-gray-700"
                        : "bg-gray-200"
                  }`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 min-h-0">
          {error && (
            <div className="mb-4 flex items-start gap-2 p-4 bg-red-500/10 border border-red-500/50 rounded-lg">
              <AlertCircle
                size={20}
                className="text-red-400 mt-0.5 flex-shrink-0"
              />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 flex items-start gap-2 p-4 bg-green-500/10 border border-green-500/50 rounded-lg">
              <CheckCircle
                size={20}
                className="text-green-400 mt-0.5 flex-shrink-0"
              />
              <p className="text-sm text-green-400">{success}</p>
            </div>
          )}

          {/* Step 1: Filter Selection */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Select Filter Preset
                </label>
                <FilterPresetSelector
                  presets={filterPresets}
                  selectedPresetId={selectedPresetId}
                  onSelectPreset={handleSelectPreset}
                  onDeletePreset={handleDeletePreset}
                  onSetDefault={handleSetDefaultPreset}
                  currentUserId={currentUser?.id}
                />
              </div>

              {advancedFilters && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info
                      size={20}
                      className="text-blue-400 mt-0.5 flex-shrink-0"
                    />
                    <div>
                      <p className="text-sm font-medium text-blue-400">
                        Filter Active
                      </p>
                      <p
                        className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {advancedFilters.conditions?.length || 0} condition(s)
                        will be applied to select leads
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Assignment Settings */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Leads Per User
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={leadsPerUser}
                  onChange={(e) => setLeadsPerUser(e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
                <p
                  className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Maximum number of leads to assign to each user
                </p>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Minimum Cycle Count
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={minCycleCount}
                  onChange={(e) => setMinCycleCount(e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
                <p
                  className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  A lead must be assigned to at least this many different users
                  before cycling back
                </p>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Assignment Strategy
                </label>
                <select
                  value={assignmentStrategy}
                  onChange={(e) => setAssignmentStrategy(e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="round_robin">
                    Round Robin (Equal Distribution)
                  </option>
                  <option value="workload_balanced">
                    Workload Balanced (By Current Load)
                  </option>
                  <option value="performance_based">
                    Performance Based (By Conversion Rate)
                  </option>
                </select>
                <p
                  className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {assignmentStrategy === "round_robin" &&
                    "Distributes leads equally among all users in rotation"}
                  {assignmentStrategy === "workload_balanced" &&
                    "Prioritizes users with fewer active leads"}
                  {assignmentStrategy === "performance_based" &&
                    "Prioritizes users with higher conversion rates"}
                </p>
              </div>
            </div>
          )}

          {/* Step 3: User Selection */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3
                    className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {teamId ? "Select Team Members" : "Select Users"} for
                    Assignment Pool
                  </h3>
                  <p
                    className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {selectedUsers.length} of {filteredUsers.length} users
                    selected
                    {selectedUsers.length < minCycleCount && (
                      <span className="text-orange-400 ml-2">
                        (Need at least {minCycleCount})
                      </span>
                    )}
                  </p>
                </div>
                <button
                  onClick={toggleAllUsers}
                  disabled={filteredUsers.length === 0}
                  className={`px-4 py-2 text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDarkMode
                      ? "bg-gray-700 hover:bg-gray-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                  }`}
                >
                  {selectedUsers.length === filteredUsers.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </div>

              <div>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div
                className={`border rounded-lg p-2 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                style={{ maxHeight: "400px", overflowY: "auto" }}
              >
                {loading ? (
                  <div
                    className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                    <p>Loading users...</p>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div
                    className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <Users size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No users found</p>
                    {teamId && (
                      <p className="text-sm mt-1">
                        Make sure team members are added to this team
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredUsers.map((user) => (
                      <label
                        key={user.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedUsers.includes(user.id)
                            ? "bg-blue-500/20 border border-blue-500/50"
                            : isDarkMode
                              ? "bg-gray-800 hover:bg-gray-700 border border-gray-700"
                              : "bg-gray-50 hover:bg-gray-100 border border-gray-200"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          className={`w-5 h-5 rounded text-blue-600 focus:ring-blue-500 ${isDarkMode ? "border-gray-600 bg-gray-700" : "border-gray-300 bg-white"}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}
                            >
                              {user.profileImageUrl ? (
                                <img
                                  src={user.profileImageUrl}
                                  alt={user.name}
                                  className="w-full h-full rounded-full object-cover"
                                />
                              ) : (
                                user.name?.charAt(0).toUpperCase()
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <div
                                  className={`text-sm font-medium truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                >
                                  {user.name}
                                </div>
                                {user.role && (
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}
                                  >
                                    {user.role}
                                  </span>
                                )}
                              </div>
                              <div
                                className={`text-xs truncate ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Preview & Execute */}
          {step === 4 && previewData && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div
                  className={`p-4 rounded-lg border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}
                >
                  <div
                    className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Total Leads Available
                  </div>
                  <div
                    className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {previewData.totalLeadsAvailable}
                  </div>
                </div>
                <div
                  className={`p-4 rounded-lg border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}
                >
                  <div
                    className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Will Be Assigned
                  </div>
                  <div className="text-2xl font-bold text-blue-400">
                    {previewData.totalToAssign}
                  </div>
                </div>
              </div>

              <div>
                <h3
                  className={`text-lg font-semibold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Assignment Preview
                </h3>
                <div
                  className="space-y-2"
                  style={{ maxHeight: "400px", overflowY: "auto" }}
                >
                  {previewData.assignments?.map((assignment) => (
                    <div
                      key={assignment.userId}
                      className={`flex items-center justify-between p-3 rounded-lg border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}
                        >
                          {assignment.user.profileImageUrl ? (
                            <img
                              src={assignment.user.profileImageUrl}
                              alt={assignment.user.name}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            assignment.user.name?.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div>
                          <div
                            className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {assignment.user.name}
                          </div>
                          <div
                            className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Current: {assignment.currentLeadCount} leads
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-blue-400">
                          +{assignment.leadsToAssign}
                        </div>
                        <div
                          className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          New total:{" "}
                          {assignment.currentLeadCount +
                            assignment.leadsToAssign}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {previewData.warnings?.length > 0 && (
                <div className="p-4 bg-orange-500/10 border border-orange-500/50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle
                      size={20}
                      className="text-orange-400 mt-0.5 flex-shrink-0"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-orange-400 mb-2">
                        Warnings
                      </p>
                      <ul
                        className={`text-sm space-y-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {previewData.warnings.map((warning, idx) => (
                          <li key={idx}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`flex items-center justify-between p-6 border-t flex-shrink-0 ${
            isDarkMode
              ? "border-gray-700 bg-gray-800/50"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            onClick={() => (step > 1 ? setStep(step - 1) : onClose())}
            disabled={loading}
            className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
              isDarkMode
                ? "text-gray-300 hover:text-white hover:bg-gray-700"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
            }`}
          >
            {step === 1 ? "Cancel" : "Back"}
          </button>

          <div className="flex items-center gap-2">
            {step < 3 && (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed() || loading}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
              >
                Next
              </button>
            )}

            {step === 3 && (
              <button
                onClick={getPreviewData}
                disabled={!canProceed() || loading}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
              >
                <Filter size={16} />
                {loading ? "Generating Preview..." : "Preview Assignment"}
              </button>
            )}

            {step === 4 && (
              <button
                onClick={executeAssignment}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
              >
                <Play size={16} />
                {loading ? "Assigning..." : "Execute Assignment"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoAssignLeadsModal;
